import { z } from "zod";
//#region src/telemetry-broker.ts
/**
* The shared contract of the CP telemetry broker and the DP telemetry route.
*
* An operator reads DP telemetry through the CP Grafana. Grafana calls the broker in
* provisioning-svc at `/v1/telemetry/dp/:dpId/<store><path>`. The broker forwards through
* `DpHttpClient` to core-api at `/internal/telemetry/<store><path>`, behind `CpMtlsGuard`, and
* core-api forwards to the DP store. Both ends run `checkTelemetryRequest`, so both ends refuse the
* same requests. core-api reads the two broker headers with `readTelemetryBrokerHeaders`.
*
* The module also holds the fixed values of the read path: the route header of the collector
* streams, the response cap, the nested timeouts, the Tempo `Accept` value and the Grafana
* datasource grammar. Every consumer imports them; no consumer restates them.
*
* Every export is a constant, a type or a pure function: no I/O, no Node API, no side effect. The
* enum schemas of the stores and the route values sit beside it in `telemetry-broker.schemas.ts`.
*/
/**
* The store segment of the broker route and of the core-api route:
* - `prom`: the DP Mimir Prometheus API (core-api maps it under the `/prometheus` prefix);
* - `tempo`: the DP Tempo;
* - `loki`: the DP Loki.
*/
const TELEMETRY_STORES = [
	"prom",
	"tempo",
	"loki"
];
/**
* The header that carries the operator id on a forwarded telemetry read: the `sub` of the
* verified `cp_token`, a UUID. The broker sets it on every forwarded read. The DP trusts it only
* behind `CpMtlsGuard` and logs it as an attribute only. Lower case, as Node gives header names.
*/
const TELEMETRY_OPERATOR_ID_HEADER = "x-zion-operator-id";
/**
* The header that carries the broker's ticket row id on a forwarded telemetry read, a UUID. The
* ticket is opaque: a random id plus audit rows, never a signed token. The DP trusts it only behind
* `CpMtlsGuard` and logs it as an attribute only. Lower case, as Node gives header names.
*/
const TELEMETRY_TICKET_ID_HEADER = "x-zion-ticket-id";
/**
* The header that the DP collector sets on its OTLP export to the CP gateway. The gateway routes
* the stream by its value. Lower case; the value is case-sensitive.
*/
const TELEMETRY_ROUTE_HEADER = "x-zion-telemetry-route";
/**
* The values of `TELEMETRY_ROUTE_HEADER`:
* - `dp`: the full stream, stored in the CP tenant of the DP;
* - `fleet`: the fleet-only stream, stored in `__cp__`.
* The gateway refuses any other value.
*/
const TELEMETRY_ROUTE_VALUES = ["dp", "fleet"];
/**
* The `Accept` value that every Tempo read sends, so Tempo answers JSON and never protobuf.
*/
const TELEMETRY_TEMPO_ACCEPT = "application/json";
/**
* The one response cap of a telemetry read, in bytes: 16 MiB. core-api, the broker and
* site-admin-api each stop a read whose response body is larger.
*/
const TELEMETRY_MAX_RESPONSE_BYTES = 16777216;
/**
* The nested timeouts of one telemetry read, in milliseconds. Each hop gives up before the hop
* that called it, so the inner error reaches the caller as an answer and never as a cut socket:
* - `store`: the DP store query timeout, 20 s;
* - `coreApi`: core-api's call to the store, 25 s;
* - `broker`: the broker's call to core-api, 27 s;
* - `siteAdminApi`: site-admin-api's call to the broker, 28 s;
* - `cpGateway`: the CP gateway request timeout, 30 s, the outer bound that no hop sets.
*/
const TELEMETRY_TIMEOUTS_MS = Object.freeze({
	store: 2e4,
	coreApi: 25e3,
	broker: 27e3,
	siteAdminApi: 28e3,
	cpGateway: 3e4
});
const PROM_RANGE_PARAMS = [
	"match[]",
	"start",
	"end",
	"limit"
];
const LOKI_LABEL_PARAMS = [
	"start",
	"end",
	"since",
	"query"
];
const TEMPO_TAG_VALUE_PARAMS = [
	"q",
	"start",
	"end",
	"limit"
];
/**
* The 21 read-only GET paths of the three stores. No write, admin, ruler or push path is on it.
* The DP Mimir `/prometheus` prefix is core-api's mapping and is not part of a template.
*/
const TELEMETRY_PATH_ALLOWLIST = Object.freeze([
	{
		store: "prom",
		template: "/api/v1/query",
		params: [
			"query",
			"time",
			"timeout",
			"limit"
		]
	},
	{
		store: "prom",
		template: "/api/v1/query_range",
		params: [
			"query",
			"start",
			"end",
			"step",
			"timeout",
			"limit"
		]
	},
	{
		store: "prom",
		template: "/api/v1/series",
		params: PROM_RANGE_PARAMS
	},
	{
		store: "prom",
		template: "/api/v1/labels",
		params: PROM_RANGE_PARAMS
	},
	{
		store: "prom",
		template: "/api/v1/label/{label}/values",
		params: PROM_RANGE_PARAMS
	},
	{
		store: "prom",
		template: "/api/v1/metadata",
		params: [
			"metric",
			"limit",
			"limit_per_metric"
		]
	},
	{
		store: "prom",
		template: "/api/v1/status/buildinfo",
		params: []
	},
	{
		store: "loki",
		template: "/loki/api/v1/query",
		params: [
			"query",
			"limit",
			"time",
			"direction"
		]
	},
	{
		store: "loki",
		template: "/loki/api/v1/query_range",
		params: [
			"query",
			"limit",
			"start",
			"end",
			"step",
			"interval",
			"direction"
		]
	},
	{
		store: "loki",
		template: "/loki/api/v1/labels",
		params: LOKI_LABEL_PARAMS
	},
	{
		store: "loki",
		template: "/loki/api/v1/label/{label}/values",
		params: LOKI_LABEL_PARAMS
	},
	{
		store: "loki",
		template: "/loki/api/v1/series",
		params: [
			"match[]",
			"start",
			"end",
			"since"
		]
	},
	{
		store: "tempo",
		template: "/api/echo",
		params: []
	},
	{
		store: "tempo",
		template: "/api/status/buildinfo",
		params: []
	},
	{
		store: "tempo",
		template: "/api/search",
		params: [
			"q",
			"tags",
			"minDuration",
			"maxDuration",
			"limit",
			"start",
			"end",
			"spss"
		]
	},
	{
		store: "tempo",
		template: "/api/traces/{traceId}",
		params: ["start", "end"]
	},
	{
		store: "tempo",
		template: "/api/v2/traces/{traceId}",
		params: ["start", "end"]
	},
	{
		store: "tempo",
		template: "/api/search/tags",
		params: [
			"scope",
			"start",
			"end"
		]
	},
	{
		store: "tempo",
		template: "/api/v2/search/tags",
		params: [
			"scope",
			"q",
			"start",
			"end",
			"limit"
		]
	},
	{
		store: "tempo",
		template: "/api/search/tag/{tag}/values",
		params: TEMPO_TAG_VALUE_PARAMS
	},
	{
		store: "tempo",
		template: "/api/v2/search/tag/{tag}/values",
		params: TEMPO_TAG_VALUE_PARAMS
	}
]);
/** The longest path `checkTelemetryRequest` admits, in UTF-8 bytes. */
const TELEMETRY_PATH_MAX_BYTES = 512;
/** The most query parameter names `checkTelemetryRequest` admits. */
const TELEMETRY_MAX_PARAMS = 32;
/** The bytes a path may hold. Every `%`, `?`, `#`, `\`, space and non-ASCII byte is outside it. */
const PATH_CHARACTERS = /^[A-Za-z0-9/_.:-]+$/;
const PLACEHOLDER_GRAMMAR = {
	"{label}": /^[A-Za-z_][A-Za-z0-9_]{0,127}$/,
	"{tag}": /^[A-Za-z0-9_.:-]{1,128}$/,
	"{traceId}": /^[0-9a-fA-F]{1,32}$/
};
const STORE_SET = new Set(TELEMETRY_STORES);
/**
* The UTF-8 byte length of `text`, counted per UTF-16 code unit: a surrogate pair is 4 bytes, so
* each surrogate counts 2. No Node or DOM API, so the module also runs in a browser bundle.
*/
function utf8ByteLength(text) {
	let bytes = 0;
	for (let index = 0; index < text.length; index += 1) {
		const unit = text.charCodeAt(index);
		if (unit <= 127) bytes += 1;
		else if (unit <= 2047 || unit >= 55296 && unit <= 57343) bytes += 2;
		else bytes += 3;
	}
	return bytes;
}
/** True when `path` has a valid shape: rooted, the allowed bytes, no empty, `.` or `..` segment. */
function isWellFormedPath(path) {
	if (!path.startsWith("/") || !PATH_CHARACTERS.test(path)) return false;
	return path.slice(1).split("/").every((segment) => segment !== "" && segment !== "." && segment !== "..");
}
/** True when the literal segments and the segment count of `template` equal those of `segments`. */
function matchesTemplate(template, segments) {
	const parts = template.slice(1).split("/");
	return parts.length === segments.length && parts.every((part, index) => part.startsWith("{") || part === segments[index]);
}
/** True when every placeholder segment of `template` matches its grammar in `segments`. */
function placeholdersMatch(template, segments) {
	return template.slice(1).split("/").every((part, index) => {
		const grammar = PLACEHOLDER_GRAMMAR[part];
		return grammar === void 0 || grammar.test(segments[index]);
	});
}
/**
* Checks one telemetry read against the store allowlist.
*
* Callers: the broker (provisioning-svc) before the access decision, and core-api before it
* forwards to the store. Both ends refuse the same requests because both run this function.
*
* The checks run in this order, and the first failure is the answer:
* 1. `store` is one of `TELEMETRY_STORES`, else `unknown_store`.
* 2. `method` is exactly `GET`, else `method_not_allowed` (`get` and `HEAD` fail).
* 3. `path` is at most 512 UTF-8 bytes, else `path_too_long`.
* 4. `path` starts with `/`, holds only `[A-Za-z0-9/_.:-]`, has no empty segment, no `.` or `..`
*    segment and no trailing `/`, else `path_malformed`. So every `%`, `?`, `#`, `\`, space and
*    non-ASCII byte fails, and the check decodes nothing.
* 5. `path` has the segment count and the literal segments of one rule of `store`, else
*    `path_not_allowed`.
* 6. Each placeholder segment matches its grammar, else `segment_malformed`.
* 7. `paramNames` has at most 32 entries, else `too_many_params`.
* 8. Each name is in the rule's `params`, else `param_not_allowed`. A name may repeat.
*
* Output on success: `ok: true`, the matched rule and the path unchanged. It never reads parameter
* values: the broker forwards them as one encoded query string and the DP store parses them. It
* never throws and has no side effect. Invariant: no input with a `..` segment, a `%` byte or a
* method other than `GET` returns `ok: true`.
*/
function checkTelemetryRequest(input) {
	if (!STORE_SET.has(input.store)) return {
		ok: false,
		reason: "unknown_store"
	};
	if (input.method !== "GET") return {
		ok: false,
		reason: "method_not_allowed"
	};
	if (utf8ByteLength(input.path) > TELEMETRY_PATH_MAX_BYTES) return {
		ok: false,
		reason: "path_too_long"
	};
	if (!isWellFormedPath(input.path)) return {
		ok: false,
		reason: "path_malformed"
	};
	const segments = input.path.slice(1).split("/");
	const rule = TELEMETRY_PATH_ALLOWLIST.find((candidate) => candidate.store === input.store && matchesTemplate(candidate.template, segments));
	if (rule === void 0) return {
		ok: false,
		reason: "path_not_allowed"
	};
	if (!placeholdersMatch(rule.template, segments)) return {
		ok: false,
		reason: "segment_malformed"
	};
	if (input.paramNames.length > TELEMETRY_MAX_PARAMS) return {
		ok: false,
		reason: "too_many_params"
	};
	if (!input.paramNames.every((name) => rule.params.includes(name))) return {
		ok: false,
		reason: "param_not_allowed"
	};
	return {
		ok: true,
		rule,
		path: input.path
	};
}
const UuidSchema = z.uuid();
/**
* Reads the operator id and the ticket id that the broker set on a forwarded telemetry read.
*
* Caller: core-api, behind `CpMtlsGuard`, before it logs and forwards. Input: Node's
* `IncomingHttpHeaders` shape, with lower-case names. The checks run in this order:
* 1. An array value of either header gives `duplicate_header`.
* 2. An absent or empty operator id gives `missing_operator_id`.
* 3. An absent or empty ticket id gives `missing_ticket_id`.
* 4. An operator id that `z.uuid()` refuses gives `malformed_operator_id`.
* 5. A ticket id that `z.uuid()` refuses gives `malformed_ticket_id`.
*
* Output on success: both values as sent. It never throws, reads no other header and has no side
* effect.
*/
function readTelemetryBrokerHeaders(headers) {
	const operatorId = headers[TELEMETRY_OPERATOR_ID_HEADER];
	const ticketId = headers[TELEMETRY_TICKET_ID_HEADER];
	if (typeof operatorId === "object" || typeof ticketId === "object") return {
		ok: false,
		reason: "duplicate_header"
	};
	if (operatorId === void 0 || operatorId === "") return {
		ok: false,
		reason: "missing_operator_id"
	};
	if (ticketId === void 0 || ticketId === "") return {
		ok: false,
		reason: "missing_ticket_id"
	};
	if (!UuidSchema.safeParse(operatorId).success) return {
		ok: false,
		reason: "malformed_operator_id"
	};
	if (!UuidSchema.safeParse(ticketId).success) return {
		ok: false,
		reason: "malformed_ticket_id"
	};
	return {
		ok: true,
		operatorId,
		ticketId
	};
}
/** The three per-DP Grafana datasources, one per signal. */
const TELEMETRY_DATASOURCE_SIGNALS = [
	"metrics",
	"logs",
	"traces"
];
/**
* The uid of every per-DP Grafana datasource: `dpm-` (metrics), `dpl-` (logs) or `dpt-` (traces),
* then the lower-case DP UUID. 40 characters, the Grafana uid limit. The Grafana datasource
* reconciler owns every datasource whose uid matches, and no other.
*/
const TELEMETRY_DATASOURCE_UID_PATTERN = /^dp[mlt]-[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const DATASOURCE_UID_PREFIX = {
	metrics: "dpm-",
	logs: "dpl-",
	traces: "dpt-"
};
const LOWER_CASE_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const SIGNAL_SET = new Set(TELEMETRY_DATASOURCE_SIGNALS);
/** Throws `TypeError` when `signal` is not a signal or `dpId` is not a lower-case UUID. */
function assertDatasourceKey(signal, dpId) {
	if (!SIGNAL_SET.has(signal)) throw new TypeError(`telemetry datasource signal ${JSON.stringify(signal)} is not metrics, logs or traces`);
	if (!LOWER_CASE_UUID.test(dpId)) throw new TypeError(`telemetry datasource DP id ${JSON.stringify(dpId)} is not a lower-case UUID`);
}
/**
* The uid of one per-DP Grafana datasource: `dpm-<dpId>`, `dpl-<dpId>` or `dpt-<dpId>`.
*
* Callers: the Grafana datasource reconciler and the boards that link to a DP. Input: a signal and
* the lower-case DP UUID. Output: the uid, which matches `TELEMETRY_DATASOURCE_UID_PATTERN`.
* Errors: a signal outside `TELEMETRY_DATASOURCE_SIGNALS`, or a DP id that is not a lower-case
* UUID, throws `TypeError`, so no code path writes a uid that the reconciler does not own.
* Pure: no side effect.
*/
function telemetryDatasourceUid(signal, dpId) {
	assertDatasourceKey(signal, dpId);
	return `${DATASOURCE_UID_PREFIX[signal]}${dpId}`;
}
/**
* The name of one per-DP Grafana datasource: `<dpName> / <signal> [<dpId>]`.
*
* The board regex `\[<dpId>\]$` selects exactly one datasource of each signal by this suffix.
* Input: the DP name, a signal and the lower-case DP UUID. Errors: an empty DP name throws
* `TypeError`, and so do the signal and DP id refusals of `telemetryDatasourceUid`. Pure: no side
* effect.
*/
function telemetryDatasourceName(dpName, signal, dpId) {
	assertDatasourceKey(signal, dpId);
	if (dpName === "") throw new TypeError("telemetry datasource DP name is empty");
	return `${dpName} / ${signal} [${dpId}]`;
}
//#endregion
export { TELEMETRY_DATASOURCE_SIGNALS, TELEMETRY_DATASOURCE_UID_PATTERN, TELEMETRY_MAX_RESPONSE_BYTES, TELEMETRY_OPERATOR_ID_HEADER, TELEMETRY_PATH_ALLOWLIST, TELEMETRY_ROUTE_HEADER, TELEMETRY_ROUTE_VALUES, TELEMETRY_STORES, TELEMETRY_TEMPO_ACCEPT, TELEMETRY_TICKET_ID_HEADER, TELEMETRY_TIMEOUTS_MS, checkTelemetryRequest, readTelemetryBrokerHeaders, telemetryDatasourceName, telemetryDatasourceUid };

//# sourceMappingURL=telemetry-broker.js.map